import React from 'react';
import {
    EntityType,
    FabricType,
    Owner,
    GlobalTags,
    GlossaryTerms,
    SearchInsight,
    Domain,
    Container,
    ParentContainersResult,
    Maybe,
    Deprecation,
    DatasetStatsSummary,
    DataProduct,
    Health,
    EntityPath,
    CustomPropertiesEntry,
} from '../../../../types.generated';
import DefaultPreviewCard from '../../../preview/DefaultPreviewCard';
import { useEntityRegistry } from '../../../useEntityRegistry';
import { capitalizeFirstLetterOnly } from '../../../shared/textUtil';
import { IconStyleType } from '../../Entity';
import { DatasetStatsSummary as DatasetStatsSummaryView } from '../shared/DatasetStatsSummary';

export const Preview = ({
    urn,
    name,
    exists,
    origin,
    description,
    platformName,
    platformLogo,
    platformNames,
    platformLogos,
    platformInstanceId,
    entityType,
    owners,
    globalTags,
    domain,
    dataProduct,
    deprecation,
    snippet,
    insights,
    glossaryTerms,
    governanceStatus,
    governanceFlag,
    subtype,
    externalUrl,
    container,
    parentContainers,
    rowCount,
    columnCount,
    sizeInBytes,
    statsSummary,
    lastUpdatedMs,
    health,
    degree,
    paths,
    customProperties,
    readOnly,
}: {
    urn: string;
    name: string;
    exists?: boolean;
    origin: FabricType;
    description?: string | null;
    platformName?: string;
    platformLogo?: string | null;
    platformNames?: (Maybe<string> | undefined)[];
    platformLogos?: (Maybe<string> | undefined)[];
    platformInstanceId?: string;
    owners?: Array<Owner> | null;
    domain?: Domain | null;
    dataProduct?: DataProduct | null;
    deprecation?: Deprecation | null;
    globalTags?: GlobalTags | null;
    governanceFlag?: any;
    governanceStatus?: any;
    snippet?: React.ReactNode | null;
    insights?: Array<SearchInsight> | null;
    glossaryTerms?: GlossaryTerms | null;
    subtype?: string | null;
    externalUrl?: string | null;
    container?: Container | null;
    entityType?: string;
    parentContainers?: ParentContainersResult | null;
    rowCount?: number | null;
    columnCount?: number | null;
    sizeInBytes?: number | null;
    statsSummary?: DatasetStatsSummary | null;
    lastUpdatedMs?: number | null;
    health?: Health[] | null;
    degree?: number;
    paths?: EntityPath[];
    customProperties?: Maybe<CustomPropertiesEntry[]>;
    readOnly?: boolean;
}): JSX.Element => {
    const entityRegistry = useEntityRegistry();
    return (
        <DefaultPreviewCard
            url={entityRegistry.getEntityUrl(EntityType.Dataset, urn)}
            name={name || ''}
            urn={urn}
            exists={exists}
            entityType={entityType}
            description={description || ''}
            type={capitalizeFirstLetterOnly(subtype) || 'Dataset'}
            logoUrl={platformLogo || ''}
            typeIcon={entityRegistry.getIcon(EntityType.Dataset, 12, IconStyleType.ACCENT)}
            platform={platformName}
            platforms={platformNames}
            logoUrls={platformLogos}
            platformInstanceId={platformInstanceId}
            qualifier={origin}
            tags={globalTags || undefined}
            owners={owners}
            domain={domain}
            dataProduct={dataProduct}
            container={container || undefined}
            deprecation={deprecation}
            governanceFlag={governanceFlag}
            governanceStatus={governanceStatus}
            snippet={snippet}
            glossaryTerms={glossaryTerms || undefined}
            insights={insights}
            parentContainers={parentContainers}
            externalUrl={externalUrl}
            topUsers={statsSummary?.topUsersLast30Days}
            subHeader={
                <DatasetStatsSummaryView
                    rowCount={rowCount}
                    columnCount={columnCount}
                    sizeInBytes={sizeInBytes}
                    lastUpdatedMs={lastUpdatedMs}
                />
            }
            health={health || undefined}
            degree={degree}
            paths={paths}
            customProperties={customProperties}
            readOnly={readOnly}
        />
    );
};
